import React, { useState, useContext, useEffect } from 'react';

import {
  Paper, Button, TextField, Dialog, DialogTitle, DialogActions,
  DialogContent, Typography, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Box
} from "@mui/material";
import { Edit, Delete, ArrowBack, Save, Cancel, Add } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import { strDateToDDMMYYYY, strDateToYYYYMMDD } from '../../functions/formatDate.js';
import { formatEuro } from '../../functions/formatNumber.js';
import AppContext from '../AppContext.js';
import { getColorForAmount } from '../../functions/colors.js';
import config from '../../config.js';
import { SelectConcepteTextField } from './SelectConcepte.js';
import ConcepteMovementDisplay from './ConcepteMovementDisplay.js';
import useBack from '../../hooks/useBack.js';

const SubdividirMovimentComponent = ({ concMov, moviment, userapp, listConceptes, onError }) => {
  const [subdivisions, setSubdivisions] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowData, setEditRowData] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { ctxUser, ctxApp, setCtxApp, conceptesList } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const { onBack } = useBack();

  // Initialize subdivisions from the movement's existing concepts
  useEffect(() => {
    if (moviment && moviment.conceptes) {
      // Sort subdivisions by ID or date for stability
      const sorted = [...moviment.conceptes].sort((a, b) => (a.id || 0) - (b.id || 0));
      setSubdivisions(sorted);
    }
  }, [moviment]);

  // Update total counts in AppContext
  const updateNumPendentTotal = () => {
    axios.get(`${config.apiBaseUrl}/v1/numtractamentmanual/${userapp}`, {
      headers: { Authorization: `${jwtToken}` }
    }).then((response) => {
      const newCtxData = {
        ...ctxApp,
        numConceptePendent: response.data?.numConceptePendent || 0,
        numDesglosPendent: response.data?.numDesglosPendent || 0,
        numPendentValidar: response.data?.numPendentValidar || 0,
        numPendentTotal: (response.data?.numConceptePendent || 0) + (response.data?.numDesglosPendent || 0) + (response.data?.numPendentValidar || 0)
      };
      setCtxApp(newCtxData);
    }).catch((err) => {
      onError(err, 'update num pendents');
    });
  };

  if (!moviment || !concMov) {
    return null;
  }

  // Sum of subdivisions
  const totalSubdivided = subdivisions.reduce((sum, item) => sum + (Number(item.importe) || 0), 0);
  const originalAmount = moviment.importOrig || 0;
  const isTotalMatch = Math.abs(originalAmount - totalSubdivided) < 0.01;
  const remaining = originalAmount - totalSubdivided;

  // Icons and colors configuration
  const targetConceptList = listConceptes || conceptesList;
  const conceptObj = targetConceptList?.find(c => c.nom === concMov.concepte);
  const isInvestment = conceptObj?.tipus === 'INVERSIO';
  const colorOriginalImport = getColorForAmount(originalAmount, isInvestment);

  // Row Edit actions
  const handleEditRow = (row) => {
    setEditingRowId(row.id);
    setEditRowData({ ...row });
  };

  const handleCancelRow = () => {
    // If it was a newly created row with no real values, remove it on cancel
    if (typeof editingRowId === 'string' && editingRowId.startsWith('temp_')) {
      const row = subdivisions.find(item => item.id === editingRowId);
      if (!row || (!row.descripcio && row.importe === 0)) {
        setSubdivisions(subdivisions.filter(item => item.id !== editingRowId));
      }
    }
    setEditingRowId(null);
    setEditRowData(null);
  };

  const handleSaveRow = (id) => {
    const hasConcept = Boolean(
      editRowData.concepte ||
      editRowData.nodeFulla ||
      editRowData.id_node_fulla ||
      editRowData.idNodeFulla ||
      editRowData.conceptePath ||
      editRowData.nodeN1 ||
      editRowData.idNodeN1
    );
    if (!hasConcept) {
      alert("Has de seleccionar un concepte");
      return;
    }
    if (editRowData.importe === "" || isNaN(Number(editRowData.importe))) {
      alert("El import ha de ser un valor numèric vàlid");
      return;
    }

    const newAmount = Number(editRowData.importe);

    // 1. Validation: Signs must match original amount
    const isOriginalNegative = originalAmount < 0;
    const isNewNegative = newAmount < 0;
    
    if (newAmount !== 0 && (isOriginalNegative !== isNewNegative)) {
      const confirmSign = window.confirm(isOriginalNegative 
        ? "L'import introduït és positiu, però el moviment original és negatiu. Vols continuar?" 
        : "L'import introduït és negatiu, però el moviment original és positiu. Vols continuar?"
      );
      if (!confirmSign) {
        return;
      }
    }

    // If it's the first row, we just save it directly (amount edit is disabled anyway, but just in case)
    const firstRowId = subdivisions[0]?.id;
    const isFirstRow = id === firstRowId;
    if (isFirstRow) {
      setSubdivisions(subdivisions.map(item => item.id === id ? { ...editRowData, importe: newAmount } : item));
      setEditingRowId(null);
      setEditRowData(null);
      return;
    }

    // 2. Calculate new amount for the first row
    const otherRowsSum = subdivisions
      .filter(item => item.id !== firstRowId && item.id !== id)
      .reduce((sum, item) => sum + (Number(item.importe) || 0), 0) + newAmount;

    const newFirstRowAmount = Math.round((originalAmount - otherRowsSum) * 100) / 100;

    // 3. Validation: Sign of the first row must also match original amount (no nos pasamos de largo)
    const isFirstRowNegative = newFirstRowAmount < 0;
    if (newFirstRowAmount !== 0 && (isOriginalNegative !== isFirstRowNegative)) {
      const confirmExceed = window.confirm("L'import introduït supera el total disponible del moviment original, forçant un canvi de signe al primer submoviment. Vols continuar?");
      if (!confirmExceed) {
        return;
      }
    }

    // Update subdivisions
    setSubdivisions(subdivisions.map(item => {
      if (item.id === id) {
        return { ...editRowData, importe: newAmount };
      }
      if (item.id === firstRowId) {
        return { ...item, importe: newFirstRowAmount };
      }
      return item;
    }));

    setEditingRowId(null);
    setEditRowData(null);
  };

  const handleDeleteRow = (id) => {
    const firstRowId = subdivisions[0]?.id;
    if (id === firstRowId) return; // Prevent deleting first row

    const updatedSubdivisions = subdivisions.filter(item => item.id !== id);

    // Recalculate first row amount based on remaining rows
    const otherRowsSum = updatedSubdivisions
      .filter(item => item.id !== firstRowId)
      .reduce((sum, item) => sum + (Number(item.importe) || 0), 0);

    const newFirstRowAmount = Math.round((originalAmount - otherRowsSum) * 100) / 100;

    setSubdivisions(updatedSubdivisions.map(item => {
      if (item.id === firstRowId) {
        return { ...item, importe: newFirstRowAmount };
      }
      return item;
    }));

    // If the deleted row was being edited, clear edit state
    if (editingRowId === id) {
      setEditingRowId(null);
      setEditRowData(null);
    }
  };

  const handleAddRow = () => {
    const newId = `temp_${Date.now()}`;
    const newRow = {
      id: newId,
      concepte: 'PENDENT',
      importe: 0,
      data: moviment.dataOrig,
      descripcio: ''
    };
    setSubdivisions([...subdivisions, newRow]);
    setEditingRowId(newId);
    setEditRowData(newRow);
  };

  // Submit all changes to backend
  const handleSaveSubdivisions = () => {
    setIsSubmitting(true);
    // Prepare payload (convert temp IDs to null and normalize node references to { id })
    const toNodeRef = (val) => {
      if (!val) return null;
      const id = typeof val === 'object' ? val.id : val;
      return id ? { id: Number(id) } : null;
    };

    const payload = subdivisions.map(sub => {
      const copy = { ...sub };
      if (typeof copy.id === 'string' && copy.id.startsWith('temp_')) {
        copy.id = null;
      }
      const isHierarchy = Boolean(copy.nodeFulla || copy.idNodeFulla || copy.id_node_fulla || copy.nodeN1 || copy.idNodeN1);
      copy.concepte = isHierarchy ? null : (copy.concepte || null);
      copy.nodeN1 = toNodeRef(copy.nodeN1 || copy.idNodeN1 || copy.id_node_n1);
      copy.nodeN2 = toNodeRef(copy.nodeN2 || copy.idNodeN2 || copy.id_node_n2);
      copy.nodeN3 = toNodeRef(copy.nodeN3 || copy.idNodeN3 || copy.id_node_n3);
      copy.nodeFulla = toNodeRef(copy.nodeFulla || copy.idNodeFulla || copy.id_node_fulla);
      return copy;
    });

    axios.put(`${config.apiBaseUrl}/v1/concmovs/moviment/${moviment.id}/subdivide/bulk`,
      payload,
      { headers: { Authorization: `${jwtToken}` } }
    ).then(() => {
      setShowSaveConfirm(false);
      updateNumPendentTotal();
      onBack();
    }).catch((err) => {
      onError(err, 'guardar subdivisions');
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  return (
    <Grid container spacing={2} padding={2}>
      {/* Header Panel */}
      <Grid size={12} display="flex" justifyContent="space-between" alignItems="center">
        <Grid display="flex" alignItems="center">
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Subdividir Moviment</Typography>
        </Grid>
      </Grid>

      {/* Original Movement Details Panel */}
      <Grid size={12}>
        <Paper sx={{ padding: '20px', mb: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>Moviment Original</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">Descripció</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{moviment.moviment}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">Data Original</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{strDateToDDMMYYYY(moviment.dataOrig)}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">Import Original</Typography>
              <Typography variant="body1" sx={{ color: colorOriginalImport, fontWeight: 'bold', fontSize: '1.2rem' }}>
                {formatEuro(originalAmount)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Subdivisions Table */}
      <Grid size={12}>
        <Paper sx={{ padding: '20px', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>Llista de Subdivisions</Typography>
          
          <TableContainer>
            <Table stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{
                  borderBottom: '2px solid #ddd',
                  '& th': {
                    backgroundColor: '#f0f0f0',
                    fontWeight: 'bold',
                    color: '#000',
                    borderBottom: 'none'
                  }
                }}>
                  <TableCell>Data</TableCell>
                  <TableCell>Concepte</TableCell>
                  <TableCell>Descripció</TableCell>
                  <TableCell align="right">Import</TableCell>
                  <TableCell align="center">Accions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subdivisions.map((row) => {
                  const isEditingThisRow = editingRowId === row.id;
                  const rowConceptObj = targetConceptList?.find(c => c.nom === row.concepte);
                  const isRowInvestment = rowConceptObj?.tipus === 'INVERSIO';
                  const colorImport = getColorForAmount(row.importe, isRowInvestment);

                  return (
                    <TableRow hover key={row.id}>
                      {isEditingThisRow ? (
                        <>
                          {/* EDITING ROW */}
                          <TableCell>
                            <TextField
                              type="date"
                              fullWidth
                              size="small"
                              value={strDateToYYYYMMDD(editRowData.data)}
                              onChange={(e) => setEditRowData({ ...editRowData, data: e.target.value })}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <SelectConcepteTextField
                              value={
                                editRowData.conceptePath ||
                                (editRowData.id_node_fulla ? `node:${editRowData.id_node_fulla}` : null) ||
                                (typeof editRowData.nodeFulla === 'number' ? `node:${editRowData.nodeFulla}` : null) ||
                                (editRowData.nodeFulla?.id ? `node:${editRowData.nodeFulla.id}` : null) ||
                                editRowData.concepte
                              }
                              onChange={(e, selectedNode) => {
                                const node = selectedNode || e.target?.node;
                                setEditRowData(prev => ({
                                  ...prev,
                                  concepte: node ? null : (e.target?.value || ''),
                                  nodeFulla: node ? node : null,
                                  id_node_fulla: node?.id || null,
                                  idNodeFulla: node?.id || null,
                                  conceptePath: node?.conceptePath || null,
                                  nodeN1: node?.nodeN1 || null,
                                  nodeN2: node?.nodeN2 || null,
                                  nodeN3: node?.nodeN3 || null,
                                  id_node_n1: node?.nodeN1?.id || null,
                                  id_node_n2: node?.nodeN2?.id || null,
                                  id_node_n3: node?.nodeN3?.id || null
                                }));
                              }}
                              listConceptes={targetConceptList}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={editRowData.descripcio || ''}
                              onChange={(e) => setEditRowData({ ...editRowData, descripcio: e.target.value })}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              disabled={row.id === subdivisions[0]?.id}
                              slotProps={{ htmlInput: { step: '0.01', style: { textAlign: 'right' } } }}
                              value={editRowData.importe}
                              onChange={(e) => setEditRowData({ ...editRowData, importe: e.target.value })}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton onClick={() => handleSaveRow(row.id)} color="primary">
                              <Save />
                            </IconButton>
                            <IconButton onClick={handleCancelRow} color="secondary">
                              <Cancel />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {/* DISPLAY ROW */}
                          <TableCell>{strDateToDDMMYYYY(row.data)}</TableCell>
                          <TableCell>
                            <ConcepteMovementDisplay movement={row} maxWidth="260px" />
                          </TableCell>
                          <TableCell>{row.descripcio || 'N/D'}</TableCell>
                          <TableCell align="right" sx={{ color: colorImport, fontWeight: 'bold' }}>
                            {formatEuro(row.importe)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              onClick={() => handleEditRow(row)} 
                              disabled={editingRowId !== null}
                              title="Editar"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteRow(row.id)} 
                              disabled={editingRowId !== null || row.id === subdivisions[0]?.id}
                              color="error"
                              title="Eliminar"
                              style={{ visibility: row.id === subdivisions[0]?.id ? 'hidden' : 'visible' }}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
                {subdivisions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No hi ha cap subdivisió. Afegeix-ne una.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Row Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddRow}
              disabled={editingRowId !== null}
            >
              Afegir Subdivisió
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* Global Validation and Action Banner */}
      <Grid size={12}>
        <Paper sx={{ padding: '20px', mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            {/* Balance display */}
            <Box>
              <Typography variant="body1">
                <strong>Total subdividit: </strong> {formatEuro(totalSubdivided)} / {formatEuro(originalAmount)}
              </Typography>
              {isTotalMatch ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  La suma dels imports és correcta.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  L'import no quadra. Diferència: <strong>{formatEuro(remaining)}</strong>
                </Alert>
              )}
            </Box>

            {/* Global Actions */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={!isTotalMatch || editingRowId !== null}
                onClick={() => setShowSaveConfirm(true)}
              >
                Guardar Subdivisions
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onBack}
              >
                Cancel·lar
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={showSaveConfirm} onClose={() => setShowSaveConfirm(false)}>
        <DialogTitle>Confirmar Subdivisions</DialogTitle>
        <DialogContent>
          <Typography>
            Estàs segur que vols guardar aquestes subdivisions? Això sobreescriurà les classificacions actuals del moviment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveConfirm(false)} disabled={isSubmitting}>
            Cancel·lar
          </Button>
          <Button onClick={handleSaveSubdivisions} color="primary" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Guardant...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SubdividirMovimentComponent;
